import React, { useState, useEffect, memo } from 'react';
import axios from 'axios';
import './GuildWarInstructions.css';
import { API_BASE_URL } from '../config/api';

const GuildWarStatus = () => {
  // 認証関連の状態
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authToken, setAuthToken] = useState('');
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(true);

  const [selectedBattle, setSelectedBattle] = useState(1);
  const [updateMessage, setUpdateMessage] = useState('');

  // タブ関連の状態
  const [battleStatusSubTab, setBattleStatusSubTab] = useState('attackStatus'); // 'battleMap' or 'attackStatus'
  const [attackStatusView, setAttackStatusView] = useState('ally'); // 'ally' or 'enemy'
  const [battleMapView, setBattleMapView] = useState('ally'); // 'ally' or 'opponent'

  // 対戦状況の状態
  const [battleStatusData, setBattleStatusData] = useState({
    ally: { 1: { towers: {} }, 2: { towers: {} }, 3: { towers: {} } },
    opponent: { 1: { towers: {} }, 2: { towers: {} }, 3: { towers: {} } }
  });

  // 選択された塔の状態
  const [selectedTowerModal, setSelectedTowerModal] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // 味方の攻撃状況
  const [allyStatus, setAllyStatus] = useState({
    1: { completed: [], pending: [], partialAttack: [] },
    2: { completed: [], pending: [], partialAttack: [] },
    3: { completed: [], pending: [], partialAttack: [] }
  });

  // 敵の攻撃状況
  const [enemyAttackStatus, setEnemyAttackStatus] = useState({
    1: { completed: [], pending: [], partialAttack: [] },
    2: { completed: [], pending: [], partialAttack: [] },
    3: { completed: [], pending: [], partialAttack: [] }
  });

  const [expandedSections, setExpandedSections] = useState({
    completed: false,
    partial: false,
    pending: false
  });

  // コンポーネントがマウントされたときにlocalStorageからトークンを取得
  useEffect(() => {
    const storedToken = localStorage.getItem('authTokenCmaStatus');
    if (storedToken) {
      setAuthToken(storedToken);
      verifyToken(storedToken);
    } else {
      setAuthLoading(false);
    }
  }, []);

  // トークンの検証
  const verifyToken = async (token) => {
    try {
      setAuthLoading(true);
      const response = await axios.post(
        `${API_BASE_URL}/api/auth/verify-token-cma-status`,
        { token }
      );

      if (response.data.success) {
        setIsAuthenticated(true);
        setAuthToken(token);
      } else {
        handleLogout();
      }
    } catch (error) {
      console.error('Token verification error:', error);
      handleLogout();
    } finally {
      setAuthLoading(false);
    }
  };

  // ログアウト処理
  const handleLogout = () => {
    localStorage.removeItem('authTokenCmaStatus');
    setAuthToken('');
    setIsAuthenticated(false);
  };

  // パスワード認証処理
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthLoading(true);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/auth/verify-password-cma-status`,
        { password }
      );

      if (response.data.success) {
        const token = response.data.token;
        localStorage.setItem('authTokenCmaStatus', token);
        setAuthToken(token);
        setIsAuthenticated(true);
      } else {
        setAuthError('Incorrect password');
      }
    } catch (error) {
      console.error('Login error:', error);
      if (error.response && error.response.status === 429) {
        setAuthError(error.response.data.message || 'Too many authentication attempts');
      } else {
        setAuthError('Incorrect password or server connection failed');
      }
    } finally {
      setAuthLoading(false);
      setPassword('');
    }
  };

  const isBattleAvailable = () => true;

  // セクションの展開/折りたたみを切り替える
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // メンバーの攻撃状況を表示するコンポーネント
  // memberはオブジェクト { name, winCount, loseCount } または文字列
  const MemberStatus = memo(({ member, status }) => {
    const getStatusColor = () => {
      switch (status) {
        case 'completed': return 'rgba(76, 175, 80, 0.1)';
        case 'partial': return 'rgba(255, 152, 0, 0.1)';
        case 'pending': return 'rgba(244, 67, 54, 0.1)';
        default: return 'transparent';
      }
    };

    // オブジェクト形式と文字列の両方に対応
    const isObject = typeof member === 'object' && member !== null;
    const name = isObject ? member.name : member;
    const winCount = isObject ? member.winCount : 0;
    const loseCount = isObject ? member.loseCount : 0;
    const maxBattles = 2; // 最大攻撃回数
    const remaining = maxBattles - winCount - loseCount;
    const winPercent = (winCount / maxBattles) * 100;
    const losePercent = (loseCount / maxBattles) * 100;
    const remainingPercent = (remaining / maxBattles) * 100;

    return (
      <div className="member-status" style={{ backgroundColor: getStatusColor() }}>
        <div className="member-info">
          <div className="member-name">{name}</div>
          {isObject && (
            <div className="win-rate-container">
              <div className="win-rate-bar" style={{ backgroundColor: '#e0e0e0' }}>
                <div className="win-rate-stacked">
                  <div
                    className="win-rate-segment win"
                    style={{ width: `${winPercent}%` }}
                  />
                  <div
                    className="win-rate-segment lose"
                    style={{ width: `${losePercent}%` }}
                  />
                </div>
              </div>
              <span className="win-rate-text">{winCount}W-{loseCount}L</span>
            </div>
          )}
        </div>
      </div>
    );
  });

  const fetchBattleStatusData = async (opponentType, battleNumber) => {
    try {
      // 認証トークンを取得
      const token = localStorage.getItem('authTokenCmaStatus');
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

      // 1. メイン塔情報をRedisから取得
      // バックエンドで既にメイン塔（remainDefenceCount === 5）を抽出してRedisに保存済み
      const enemyTowersResponse = await axios.get(
        `${API_BASE_URL}/api/guild-war-instructions-cma/enemy-towers`,
        {
          params: { battleNumber },
          headers: authHeaders
        }
      );

      const enemyTowersData = enemyTowersResponse.data.towers || {};
      const mainTowerMap = {};

      // Redisから取得したメイン塔情報を使用してメイン塔マップを作成
      Object.keys(enemyTowersData).forEach(towerNum => {
        const defenders = enemyTowersData[towerNum] || [];
        const defendersArray = Array.isArray(defenders) ? defenders : [];
        const isMainTower = defendersArray.some(def => {
          if (typeof def === 'string') return false;
          return def && (parseInt(def.remainDefenceCount, 10) === 5 || def.isMain === true);
        });
        mainTowerMap[towerNum] = isMainTower;
      });

      // 2. 実際のデータを取得
      const now = new Date();
      const requestDate = now.toISOString().split('T')[0];

      // 10塔分のデータを並列で取得
      const towerPromises = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(towerNum =>
        axios.get(
          `${API_BASE_URL}/api/guild-war-cma`,
          {
            params: {
              date: requestDate,
              opponent: opponentType,
              match: battleNumber,
              tower: towerNum
            },
            headers: authHeaders
          }
        ).catch(error => {
          console.error(`Tower ${towerNum} fetch error:`, error);
          return null;
        })
      );

      const responses = await Promise.all(towerPromises);

      const towersData = {};

      responses.forEach((response, index) => {
        if (response && response.data) {
          const towerNum = index + 1;
          const defenders = response.data.result?.guildWarDefenceDeckResponses || [];

          // メイン塔の判定（Redis情報を優先、なければ実際のデータから判定）
          // 1. まずRedisから取得したメイン塔マップを使用（キーを文字列に変換して参照）
          let isMainTower = mainTowerMap[String(towerNum)] || false;

          // 2. Redis情報がない場合は、実際のデータから判定（フォールバック）
          if (!isMainTower) {
            isMainTower = defenders.some(def => parseInt(def.remainDefenceCount, 10) === 5 || def.isMain === true);
          }

          // 残り攻撃回数の計算
          let remainingAttacks = 0;
          defenders.forEach(def => {
            remainingAttacks += (def.remainDefenceCount || 0);
          });

          // 分母の計算
          const maxAttacks = isMainTower ? defenders.length * 5 : defenders.length * 3;

          // 防衛者の情報を保存
          const defendersList = defenders.map(def => ({
            name: def.mergedGuildPlayer?.name || '不明',
            remainDefenceCount: def.remainDefenceCount || 0,
            defenseCount: def.winCount || 0,
            pvpLeaguePhase: def.mergedGuildPlayer?.pvpLeaguePhase || '不明'
          }));

          // ステータスの判定
          const totalWinCount = defendersList.reduce((sum, def) => sum + def.defenseCount, 0);
          const isDestroyed = remainingAttacks === 0;
          const isUnderAttack = remainingAttacks < maxAttacks && remainingAttacks > 0;
          const isDefending = totalWinCount > 0 && !isUnderAttack && !isDestroyed;

          towersData[towerNum] = {
            remainingAttacks,
            maxAttacks,
            isMainTower,
            defenders: defendersList,
            defenderCount: defenders.length,
            isDestroyed,
            isUnderAttack,
            isDefending
          };
        }
      });

      return { towers: towersData };
    } catch (error) {
      console.error('Failed to fetch battle status data:', error);
      return { towers: {} };
    }
  };

  const updateBattleStatus = async (opponentType) => {
    try {
      setIsLoading(true);
      const data = await fetchBattleStatusData(opponentType, selectedBattle);

      setBattleStatusData(prev => ({
        ...prev,
        [opponentType]: {
          ...prev[opponentType],
          [selectedBattle]: data
        }
      }));
    } catch (error) {
      console.error('Failed to update battle status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // 味方の状態を取得する関数
  const fetchAllyStatus = async () => {
    try {
      setUpdateMessage('Updating data...');

      // 認証トークンを取得
      const token = localStorage.getItem('authTokenCmaStatus');
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

      await axios.get(
        `${API_BASE_URL}/api/guild-war-instructions-cma/contribution/5be953486a32da5b2aff5a7b8f8833d1`,
        { headers: authHeaders }
      );

      const response = await axios.get(
        `${API_BASE_URL}/api/guild-war-instructions-cma/attack-status/5be953486a32da5b2aff5a7b8f8833d1/${selectedBattle}`,
        { headers: authHeaders }
      );

      if (response.data) {
        setAllyStatus(prev => ({
          ...prev,
          [selectedBattle]: response.data
        }));
      }

      setUpdateMessage('✅ Update completed');
      setTimeout(() => setUpdateMessage(''), 3000);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      setUpdateMessage('❌ Update failed');
      setTimeout(() => setUpdateMessage(''), 3000);
    }
  };

  // 敵の攻撃状況を取得する関数
  const fetchEnemyAttackStatus = async () => {
    try {
      setUpdateMessage('Updating enemy attack status...');

      // 認証トークンを取得
      const token = localStorage.getItem('authTokenCmaStatus');
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

      // all-towers APIで全戦闘のgidを取得
      const allTowersResponse = await axios.get(
        `${API_BASE_URL}/api/guild-war-instructions-cma/all-towers`,
        { headers: authHeaders }
      );
      const battleData = allTowersResponse.data;
      const gid = battleData[selectedBattle.toString()]?.gid;

      if (gid) {
        await axios.get(
          `${API_BASE_URL}/api/guild-war-instructions-cma/contribution/${gid}`,
          { headers: authHeaders }
        );

        const attackStatusResponse = await axios.get(
          `${API_BASE_URL}/api/guild-war-instructions-cma/attack-status/${gid}/${selectedBattle}`,
          { headers: authHeaders }
        );

        if (attackStatusResponse.data) {
          setEnemyAttackStatus(prev => ({
            ...prev,
            [selectedBattle]: attackStatusResponse.data
          }));
          setUpdateMessage('✅ Update completed');
        } else {
          setUpdateMessage('❌ Failed to fetch attack status');
        }
      } else {
        setUpdateMessage('❌ Enemy guild data not available yet');
      }

      setTimeout(() => setUpdateMessage(''), 3000);
    } catch (error) {
      console.error('Failed to update enemy attack status:', error);
      setUpdateMessage('❌ Update failed');
      setTimeout(() => setUpdateMessage(''), 3000);
    }
  };

  // 全ての戦闘の攻撃状況を自動取得する関数
  const fetchAllBattleStatus = async () => {
    try {
      console.log('Fetching all battle attack status...');

      // 認証トークンを取得
      const token = localStorage.getItem('authTokenCmaStatus');
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

      // まず味方のcontributionを取得
      await axios.get(
        `${API_BASE_URL}/api/guild-war-instructions-cma/contribution/5be953486a32da5b2aff5a7b8f8833d1`,
        { headers: authHeaders }
      );

      // 1-3戦目全ての味方の攻撃状況を取得
      const allyStatusPromises = [1, 2, 3].map(battleNumber =>
        axios.get(
          `${API_BASE_URL}/api/guild-war-instructions-cma/attack-status/5be953486a32da5b2aff5a7b8f8833d1/${battleNumber}`,
          { headers: authHeaders }
        ).catch(error => {
          console.error(`Battle ${battleNumber} ally attack status fetch failed:`, error);
          return { data: { completed: [], pending: [], partialAttack: [] } };
        })
      );

      const [response1, response2, response3] = await Promise.all(allyStatusPromises);

      setAllyStatus({
        1: response1.data || { completed: [], pending: [], partialAttack: [] },
        2: response2.data || { completed: [], pending: [], partialAttack: [] },
        3: response3.data || { completed: [], pending: [], partialAttack: [] }
      });

      // 全戦闘のgidを1回で取得（超シンプル！）
      const allTowersResponse = await axios.get(
        `${API_BASE_URL}/api/guild-war-instructions-cma/all-towers`,
        { headers: authHeaders }
      );
      const battleData = allTowersResponse.data; // { "1": { gid, guildName, ... }, "2": {...}, "3": {...} }

      // 1-3戦目全ての敵の攻撃状況を取得
      const enemyStatusPromises = [1, 2, 3].map(async battleNumber => {
        try {
          const gid = battleData[battleNumber.toString()]?.gid;

          if (!gid) {
            console.log(`Battle ${battleNumber}: Enemy guild data not available yet`);
            return {
              battleNumber,
              data: { completed: [], pending: [], partialAttack: [] }
            };
          }

          // 敵のcontributionを取得
          await axios.get(
            `${API_BASE_URL}/api/guild-war-instructions-cma/contribution/${gid}`,
            { headers: authHeaders }
          );

          // 敵の攻撃状況を取得
          const attackStatusResponse = await axios.get(
            `${API_BASE_URL}/api/guild-war-instructions-cma/attack-status/${gid}/${battleNumber}`,
            { headers: authHeaders }
          );

          return {
            battleNumber,
            data: attackStatusResponse.data || { completed: [], pending: [], partialAttack: [] }
          };
        } catch (error) {
          console.error(`Battle ${battleNumber} enemy attack status fetch failed:`, error);
          return {
            battleNumber,
            data: { completed: [], pending: [], partialAttack: [] }
          };
        }
      });

      const enemyResults = await Promise.all(enemyStatusPromises);

      const newEnemyAttackStatus = { ...enemyAttackStatus };
      enemyResults.forEach(result => {
        newEnemyAttackStatus[result.battleNumber] = result.data;
      });

      setEnemyAttackStatus(newEnemyAttackStatus);

      console.log('All battle attack status fetched successfully');
    } catch (error) {
      console.error('Failed to auto-fetch attack status:', error);
    }
  };

  // コンポーネントマウント時に全ての戦闘の攻撃状況を取得
  useEffect(() => {
    fetchAllBattleStatus();

    // 初期バトルが選択不可の場合、選択可能なバトルを自動選択
    if (!isBattleAvailable(selectedBattle)) {
      // 選択可能なバトルを見つけて設定
      for (let i = 1; i <= 3; i++) {
        if (isBattleAvailable(i)) {
          setSelectedBattle(i);
          break;
        }
      }
    }
  }, []);

  useEffect(() => {
    if (attackStatusView === 'ally') {
      fetchAllyStatus();
    }
  }, [selectedBattle]);

  // タブナビゲーション
  const TabNavigation = () => {
    return (
      <div className="subtab-navigation">
        <button
          className={`subtab-button ${battleStatusSubTab === 'attackStatus' ? 'active' : ''}`}
          onClick={() => setBattleStatusSubTab('attackStatus')}
        >
          Attack Status
        </button>
        <button
          className={`subtab-button ${battleStatusSubTab === 'battleMap' ? 'active' : ''}`}
          onClick={() => setBattleStatusSubTab('battleMap')}
        >
          Battle Map
        </button>
      </div>
    );
  };

  // 攻撃状況タブの内容
  const AttackStatusTab = () => {
    return (
      <div className="attack-status-tab">
        <div className="subtab-navigation-inner">
          <button
            className={`subtab-button ${attackStatusView === 'ally' ? 'active' : ''}`}
            onClick={() => setAttackStatusView('ally')}
          >
            Ally
          </button>
          <button
            className={`subtab-button ${attackStatusView === 'enemy' ? 'active' : ''}`}
            onClick={() => setAttackStatusView('enemy')}
          >
            Opponent
          </button>
        </div>
        {attackStatusView === 'ally' ? <AllyAttackContent /> : <EnemyAttackContent />}
      </div>
    );
  };

  // 味方の攻撃状況コンテンツ
  const AllyAttackContent = () => {
    // ギルド全体の統計を計算
    const calculateGuildStats = () => {
      const currentStatus = allyStatus[selectedBattle];
      if (!currentStatus) {
        return { totalWins: 0, totalLosses: 0, totalNoAttacks: 0, totalMembers: 0 };
      }

      let totalWins = 0;
      let totalLosses = 0;
      let totalMembers = 0;

      // 完了メンバー (2/2)
      if (currentStatus.completed) {
        currentStatus.completed.forEach(member => {
          totalWins += member.winCount || 0;
          totalLosses += member.loseCount || 0;
          totalMembers++;
        });
      }

      // 一部攻撃メンバー (1/2)
      if (currentStatus.partialAttack) {
        currentStatus.partialAttack.forEach(member => {
          totalWins += member.winCount || 0;
          totalLosses += member.loseCount || 0;
          totalMembers++;
        });
      }

      // 未攻撃メンバー (0/2)
      if (currentStatus.pending) {
        totalMembers += currentStatus.pending.length;
      }

      const maxAttacks = totalMembers * 2;
      const totalNoAttacks = maxAttacks - totalWins - totalLosses;

      return { totalWins, totalLosses, totalNoAttacks, totalMembers, maxAttacks };
    };

    const guildStats = calculateGuildStats();
    const winPercent = guildStats.maxAttacks > 0 ? (guildStats.totalWins / guildStats.maxAttacks) * 100 : 0;
    const losePercent = guildStats.maxAttacks > 0 ? (guildStats.totalLosses / guildStats.maxAttacks) * 100 : 0;
    const noAttackPercent = guildStats.maxAttacks > 0 ? (guildStats.totalNoAttacks / guildStats.maxAttacks) * 100 : 0;

    return (
      <div className="ally-tab">
        <div className="controls">
          <button onClick={fetchAllyStatus} className="refresh-button">
            Refresh
          </button>
          {updateMessage && (
            <p className={`update-message ${updateMessage.includes('✅') ? 'success' : 'error'}`}>
              {updateMessage}
            </p>
          )}
        </div>

        {/* ギルド全体の統計 */}
        <div className="guild-stats-container">
          <h3 className="guild-stats-title">Guild Overall Stats</h3>
          <div className="guild-stats-card">
            <div className="guild-stats-bar-container">
              <div className="guild-stats-bar">
                <div className="guild-stats-stacked">
                  <div
                    className="guild-stats-segment win"
                    style={{ width: `${winPercent}%` }}
                    title={`Wins: ${guildStats.totalWins}`}
                  />
                  <div
                    className="guild-stats-segment lose"
                    style={{ width: `${losePercent}%` }}
                    title={`Losses: ${guildStats.totalLosses}`}
                  />
                  <div
                    className="guild-stats-segment no-attack"
                    style={{ width: `${noAttackPercent}%` }}
                    title={`No Attacks: ${guildStats.totalNoAttacks}`}
                  />
                </div>
              </div>
            </div>
            <div className="guild-stats-details">
              <div className="stat-item win">
                <span className="stat-label">Wins</span>
                <span className="stat-value">{guildStats.totalWins}</span>
              </div>
              <div className="stat-item lose">
                <span className="stat-label">Losses</span>
                <span className="stat-value">{guildStats.totalLosses}</span>
              </div>
              <div className="stat-item no-attack">
                <span className="stat-label">None</span>
                <span className="stat-value">{guildStats.totalNoAttacks}</span>
              </div>
              <div className="stat-item total">
                <span className="stat-label">Total</span>
                <span className="stat-value">{guildStats.maxAttacks}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="battle-section">
          <div className="battle-status">
            <div className="status-column">
              <div className="status-header" onClick={() => toggleSection('completed')}>
                <h4>Completed (2/2) ({allyStatus[selectedBattle]?.completed?.length || 0})</h4>
                <span className="toggle-icon">{expandedSections.completed ? '▼' : '▶'}</span>
              </div>
              {expandedSections.completed && (
                <ul className="status-list completed">
                  {allyStatus[selectedBattle]?.completed?.map((memberData, index) => (
                    <li key={index}>
                      <MemberStatus member={memberData} status="completed" />
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="status-column">
              <div className="status-header" onClick={() => toggleSection('partial')}>
                <h4>Partial (1/2) ({allyStatus[selectedBattle]?.partialAttack?.length || 0})</h4>
                <span className="toggle-icon">{expandedSections.partial ? '▼' : '▶'}</span>
              </div>
              {expandedSections.partial && (
                <ul className="status-list partial">
                  {allyStatus[selectedBattle]?.partialAttack?.map((memberData, index) => (
                    <li key={index}>
                      <MemberStatus member={memberData} status="partial" />
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="status-column">
              <div className="status-header" onClick={() => toggleSection('pending')}>
                <h4>Pending (0/2) ({allyStatus[selectedBattle]?.pending?.length || 0})</h4>
                <span className="toggle-icon">{expandedSections.pending ? '▼' : '▶'}</span>
              </div>
              {expandedSections.pending && (
                <ul className="status-list pending">
                  {allyStatus[selectedBattle]?.pending?.map((memberData, index) => (
                    <li key={index}>
                      <MemberStatus member={memberData} status="pending" />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // 敵の攻撃状況コンテンツ
  const EnemyAttackContent = () => {
    const [localExpandedSections, setLocalExpandedSections] = useState({
      completed: false,
      partial: false,
      pending: false
    });

    const toggleLocalSection = (section) => {
      setLocalExpandedSections(prev => ({
        ...prev,
        [section]: !prev[section]
      }));
    };

    // ギルド全体の統計を計算
    const calculateGuildStats = () => {
      const currentStatus = enemyAttackStatus[selectedBattle];
      if (!currentStatus) {
        return { totalWins: 0, totalLosses: 0, totalNoAttacks: 0, totalMembers: 0 };
      }

      let totalWins = 0;
      let totalLosses = 0;
      let totalMembers = 0;

      // 完了メンバー (2/2)
      if (currentStatus.completed) {
        currentStatus.completed.forEach(member => {
          totalWins += member.winCount || 0;
          totalLosses += member.loseCount || 0;
          totalMembers++;
        });
      }

      // 一部攻撃メンバー (1/2)
      if (currentStatus.partialAttack) {
        currentStatus.partialAttack.forEach(member => {
          totalWins += member.winCount || 0;
          totalLosses += member.loseCount || 0;
          totalMembers++;
        });
      }

      // 未攻撃メンバー (0/2)
      if (currentStatus.pending) {
        totalMembers += currentStatus.pending.length;
      }

      const maxAttacks = totalMembers * 2;
      const totalNoAttacks = maxAttacks - totalWins - totalLosses;

      return { totalWins, totalLosses, totalNoAttacks, totalMembers, maxAttacks };
    };

    const guildStats = calculateGuildStats();
    const winPercent = guildStats.maxAttacks > 0 ? (guildStats.totalWins / guildStats.maxAttacks) * 100 : 0;
    const losePercent = guildStats.maxAttacks > 0 ? (guildStats.totalLosses / guildStats.maxAttacks) * 100 : 0;
    const noAttackPercent = guildStats.maxAttacks > 0 ? (guildStats.totalNoAttacks / guildStats.maxAttacks) * 100 : 0;

    return (
      <div className="enemy-attack-tab">
        <div className="controls">
          <button onClick={fetchEnemyAttackStatus} className="refresh-button">
            Refresh
          </button>
          {updateMessage && (
            <p className={`update-message ${updateMessage.includes('✅') ? 'success' : 'error'}`}>
              {updateMessage}
            </p>
          )}
        </div>

        {/* ギルド全体の統計 */}
        <div className="guild-stats-container">
          <h3 className="guild-stats-title">Guild Overall Stats</h3>
          <div className="guild-stats-card">
            <div className="guild-stats-bar-container">
              <div className="guild-stats-bar">
                <div className="guild-stats-stacked">
                  <div
                    className="guild-stats-segment win"
                    style={{ width: `${winPercent}%` }}
                    title={`Wins: ${guildStats.totalWins}`}
                  />
                  <div
                    className="guild-stats-segment lose"
                    style={{ width: `${losePercent}%` }}
                    title={`Losses: ${guildStats.totalLosses}`}
                  />
                  <div
                    className="guild-stats-segment no-attack"
                    style={{ width: `${noAttackPercent}%` }}
                    title={`No Attacks: ${guildStats.totalNoAttacks}`}
                  />
                </div>
              </div>
            </div>
            <div className="guild-stats-details">
              <div className="stat-item win">
                <span className="stat-label">Wins</span>
                <span className="stat-value">{guildStats.totalWins}</span>
              </div>
              <div className="stat-item lose">
                <span className="stat-label">Losses</span>
                <span className="stat-value">{guildStats.totalLosses}</span>
              </div>
              <div className="stat-item no-attack">
                <span className="stat-label">None</span>
                <span className="stat-value">{guildStats.totalNoAttacks}</span>
              </div>
              <div className="stat-item total">
                <span className="stat-label">Total</span>
                <span className="stat-value">{guildStats.maxAttacks}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="battle-section">
          <div className="battle-status">
            <div className="status-column">
              <div className="status-header" onClick={() => toggleLocalSection('completed')}>
                <h4>Completed (2/2) ({enemyAttackStatus[selectedBattle]?.completed?.length || 0})</h4>
                <span className="toggle-icon">{localExpandedSections.completed ? '▼' : '▶'}</span>
              </div>
              {localExpandedSections.completed && (
                <ul className="status-list completed">
                  {enemyAttackStatus[selectedBattle]?.completed?.map((memberData, index) => (
                    <li key={index}>
                      <MemberStatus member={memberData} status="completed" />
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="status-column">
              <div className="status-header" onClick={() => toggleLocalSection('partial')}>
                <h4>Partial (1/2) ({enemyAttackStatus[selectedBattle]?.partialAttack?.length || 0})</h4>
                <span className="toggle-icon">{localExpandedSections.partial ? '▼' : '▶'}</span>
              </div>
              {localExpandedSections.partial && (
                <ul className="status-list partial">
                  {enemyAttackStatus[selectedBattle]?.partialAttack?.map((memberData, index) => (
                    <li key={index}>
                      <MemberStatus member={memberData} status="partial" />
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="status-column">
              <div className="status-header" onClick={() => toggleLocalSection('pending')}>
                <h4>Pending (0/2) ({enemyAttackStatus[selectedBattle]?.pending?.length || 0})</h4>
                <span className="toggle-icon">{localExpandedSections.pending ? '▼' : '▶'}</span>
              </div>
              {localExpandedSections.pending && (
                <ul className="status-list pending">
                  {enemyAttackStatus[selectedBattle]?.pending?.map((memberData, index) => (
                    <li key={index}>
                      <MemberStatus member={memberData} status="pending" />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // 対戦状況タブの内容
  const BattleStatusTab = () => {
    const [updateMessage, setUpdateMessage] = useState('');

    const currentData = battleStatusData[battleMapView]?.[selectedBattle] || { towers: {} };
    const hasData = Object.keys(currentData.towers).length > 0;

    const handleRefresh = async () => {
      try {
        setUpdateMessage('Updating data...');
        await updateBattleStatus(battleMapView);
        setUpdateMessage('✅ Update completed');
        setTimeout(() => setUpdateMessage(''), 3000);
      } catch (error) {
        setUpdateMessage('❌ Update failed');
        setTimeout(() => setUpdateMessage(''), 3000);
      }
    };

    const handleShowData = async () => {
      try {
        setUpdateMessage('Fetching data...');
        await updateBattleStatus(battleMapView);
        setUpdateMessage('✅ Fetch completed');
        setTimeout(() => setUpdateMessage(''), 3000);
      } catch (error) {
        setUpdateMessage('❌ Fetch failed');
        setTimeout(() => setUpdateMessage(''), 3000);
      }
    };

    const handleTowerClick = (towerId) => {
      const towerData = currentData.towers[towerId];
      if (towerData && towerData.defenders && towerData.defenders.length > 0) {
        setSelectedTowerModal({ towerId, data: towerData });
      }
    };

    const closeModal = () => {
      setSelectedTowerModal(null);
    };

    const floors = {
      floor1: [1, 2, 3, 4, 5, 6],
      floor2: [7, 8, 9],
      floor3: [10]
    };

    return (
      <div className="battle-status-tab">
        <div className="subtab-navigation-inner">
          <button
            className={`subtab-button ${battleMapView === 'ally' ? 'active' : ''}`}
            onClick={() => setBattleMapView('ally')}
          >
            Ally
          </button>
          <button
            className={`subtab-button ${battleMapView === 'opponent' ? 'active' : ''}`}
            onClick={() => setBattleMapView('opponent')}
          >
            Opponent
          </button>
        </div>

        {!hasData && !isLoading ? (
          <div className="show-data-container">
            <button onClick={handleShowData} className="show-data-button">
              Show Data
            </button>
            {updateMessage && (
              <p className={`update-message ${updateMessage.includes('✅') ? 'success' : 'error'}`}>
                {updateMessage}
              </p>
            )}
          </div>
        ) : isLoading && !hasData ? (
          <div className="show-data-container">
            <p className="update-message">Loading data...</p>
          </div>
        ) : (
          <>
            <div className="controls">
              <button onClick={handleRefresh} className="refresh-button">
                Refresh
              </button>
              {updateMessage && (
                <p className={`update-message ${updateMessage.includes('✅') ? 'success' : 'error'}`}>
                  {updateMessage}
                </p>
              )}
            </div>

            <div className="battle-map">
              {Object.entries(floors).map(([floorKey, towerIds]) => {
                const floorNumber = floorKey === 'floor1' ? 1 : floorKey === 'floor2' ? 2 : 3;
                return (
                  <div key={floorKey} className="floor-section" data-floor={floorNumber}>
                    <h3 className="floor-title">Floor {floorNumber}</h3>
                    <div className="towers-grid-status">
                      {towerIds.map(towerId => {
                        const towerData = currentData.towers[towerId] || {
                          remainingAttacks: 0,
                          maxAttacks: 0,
                          isMainTower: false,
                          defenders: [],
                          isDestroyed: false,
                          isUnderAttack: false,
                          isDefending: false
                        };

                        let statusClass = '';
                        if (towerData.isDestroyed) {
                          statusClass = 'destroyed';
                        } else if (towerData.isUnderAttack) {
                          statusClass = 'under-attack';
                        } else if (towerData.isDefending) {
                          statusClass = 'defending';
                        }

                        return (
                          <div
                            key={towerId}
                            className={`tower-status-card ${statusClass} ${towerData.defenders?.length > 0 ? 'clickable' : ''}`}
                            onClick={() => handleTowerClick(towerId)}
                          >
                            <div className="tower-header">
                              <h4>
                                Tower {towerId}
                                {towerData.isMainTower && <span className="main-tower-badge">Main</span>}
                              </h4>
                            </div>
                            <div className="tower-attacks">
                              <span className="attack-count">
                                {towerData.remainingAttacks}/{towerData.maxAttacks}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {selectedTowerModal && (
              <div className="modal-overlay" onClick={closeModal}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h3>
                      Tower {selectedTowerModal.towerId} Defenders ({selectedTowerModal.data.defenderCount})
                      {selectedTowerModal.data.isMainTower && <span className="main-tower-badge">Main</span>}
                    </h3>
                    <button className="close-button" onClick={closeModal}>×</button>
                  </div>
                  <div className="defenders-list-modal">
                    {selectedTowerModal.data.defenders && selectedTowerModal.data.defenders.length > 0 ? (
                      selectedTowerModal.data.defenders.map((defender, index) => {
                        const maxDefence = selectedTowerModal.data.isMainTower ? 5 : 3;
                        return (
                          <div key={index} className="defender-item">
                            <div className="defender-info">
                              <span className="defender-name">{defender.name}</span>
                            </div>
                            <div className="defender-stats">
                              <div className="defender-stat-item">
                                <span className="defender-stat-label">WIN :</span>
                                <span className="defender-stat-value win-count">{defender.defenseCount}</span>
                              </div>
                              <div className="defender-stat-item">
                                <span className="defender-stat-value">{defender.remainDefenceCount}/{maxDefence}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <p className="no-defenders">No defender information</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        <style jsx>{`
          .show-data-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 40px 20px;
            gap: 16px;
          }

          .show-data-button {
            padding: 12px 32px;
            font-size: 16px;
            font-weight: 600;
            background: #4a90e2;
            color: white;
            border: none;
            border-radius: 8px;
            cursor: pointer;
            transition: background 0.2s, transform 0.1s;
          }

          .show-data-button:hover {
            background: #357abd;
            transform: translateY(-2px);
          }

          .show-data-button:active {
            transform: translateY(0);
          }

          .alert-message {
            background: #fff3cd;
            border: 2px solid #ffc107;
            color: #856404;
            padding: 16px 24px;
            border-radius: 8px;
            font-size: 16px;
            font-weight: 500;
            text-align: center;
          }

          .subtab-navigation-inner {
            display: flex;
            justify-content: center;
            margin-bottom: 16px;
            background: #e8e8e8;
            border-radius: 6px;
            padding: 6px;
          }

          .subtab-navigation-inner .subtab-button {
            min-width: 120px;
            text-align: center;
          }

          .battle-map {
            margin-top: 20px;
          }

          .towers-grid-status {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
            gap: 16px;
            margin-top: 16px;
          }

          .tower-status-card {
            background: white;
            border-radius: 8px;
            padding: 16px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            text-align: center;
            transition: transform 0.2s, box-shadow 0.2s;
          }

          .tower-status-card.clickable {
            cursor: pointer;
          }

          .tower-status-card.clickable:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 8px rgba(0,0,0,0.15);
          }

          .tower-status-card.destroyed {
            border: 2px solid #757575;
            background: #e0e0e0;
            opacity: 0.8;
          }

          .tower-status-card.under-attack {
            border: 2px solid #f44336;
            background: #ffebee;
          }

          .tower-status-card.defending {
            border: 2px solid #ffcdd2;
            background: #fff5f5;
          }

          .tower-header {
            margin-bottom: 8px;
          }

          .tower-header h4 {
            margin: 0 0 12px 0;
            font-size: 18px;
            color: #333;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
          }

          .main-tower-badge {
            background-color: #9e9e9e;
            color: white;
            font-size: 12px;
            padding: 2px 8px;
            border-radius: 4px;
            font-weight: bold;
          }

          .tower-attacks {
            font-size: 24px;
            font-weight: bold;
            color: #4a90e2;
          }

          .attack-count {
            display: block;
          }

          .floor-section {
            margin-bottom: 32px;
          }

          .floor-title {
            font-size: 1.4em;
            margin-bottom: 16px;
            color: #333;
            border-bottom: 2px solid #4a90e2;
            padding-bottom: 8px;
          }

          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 1000;
          }

          .modal-content {
            background: white;
            border-radius: 12px;
            padding: 24px;
            max-width: 500px;
            width: 90%;
            max-height: 80vh;
            overflow-y: auto;
            box-shadow: 0 4px 20px rgba(0,0,0,0.3);
          }

          .modal-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;
            border-bottom: 2px solid #f0f0f0;
            padding-bottom: 12px;
          }

          .modal-header h3 {
            margin: 0;
            font-size: 20px;
            color: #333;
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .close-button {
            background: none;
            border: none;
            font-size: 28px;
            cursor: pointer;
            color: #666;
            padding: 0;
            width: 32px;
            height: 32px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 4px;
            transition: background-color 0.2s;
          }

          .close-button:hover {
            background-color: #f0f0f0;
          }

          .defenders-list-modal {
            display: flex;
            flex-direction: column;
            gap: 12px;
          }

          .no-defenders {
            text-align: center;
            color: #999;
            padding: 20px;
            font-size: 14px;
          }

          .defender-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 12px;
            background: #f9f9f9;
            border-radius: 8px;
            transition: background-color 0.2s;
          }

          .defender-item:hover {
            background-color: #f0f0f0;
          }

          .defender-info {
            display: flex;
            flex-direction: column;
            gap: 4px;
          }

          .defender-name {
            font-weight: 600;
            font-size: 16px;
            color: #333;
          }

          .defender-stats {
            display: flex;
            flex-direction: column;
            gap: 4px;
            align-items: flex-end;
          }

          .defender-stat-item {
            display: flex;
            flex-direction: row;
            gap: 4px;
            align-items: center;
            border: none;
            background: none;
            padding: 0;
          }

          .defender-stat-label {
            font-size: 12px;
            color: #666;
          }

          .defender-stat-value {
            font-weight: 600;
            color: #4a90e2;
            font-size: 14px;
          }

          .win-count {
            font-size: 20px;
            color: #28a745;
            font-weight: 700;
          }
            padding: 20px;
          }
        `}</style>
      </div>
    );
  };

  // 認証ローディング中の表示
  if (authLoading) {
    return (
      <div className="guild-war-instructions">
        <h2 className="page-title">CMA Guild War Status</h2>
        <div className="loading-container">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // 未認証の場合はログインフォームを表示
  if (!isAuthenticated) {
    return (
      <div className="guild-war-instructions">
        <h2 className="page-title">CMA Guild War Status</h2>
        <div className="login-container">
          <form onSubmit={handlePasswordSubmit} className="login-form">
            <label>Enter password:</label>
            {/* Hidden username field for Chrome password manager differentiation */}
            <input
              type="text"
              name="username"
              autoComplete="username"
              value="cma-status"
              readOnly
              style={{ display: 'none' }}
            />
            <input
              type="password"
              name="password-cma-status"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={authLoading}
              placeholder="Password"
            />
            <button type="submit" disabled={authLoading}>
              {authLoading ? 'Logging in...' : 'Submit'}
            </button>
            {authError && <p className="auth-error">{authError}</p>}
          </form>
        </div>
        <style jsx>{`
          .login-container {
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 200px;
            padding: 20px;
          }

          .login-form {
            display: flex;
            flex-direction: column;
            gap: 16px;
            padding: 32px;
            background: white;
            border-radius: 12px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
            max-width: 400px;
            width: 100%;
          }

          .login-form label {
            font-weight: 500;
            color: #333;
          }

          .login-form input {
            padding: 12px 16px;
            border: 1px solid #ddd;
            border-radius: 8px;
            font-size: 16px;
            transition: border-color 0.2s;
          }

          .login-form input:focus {
            outline: none;
            border-color: #4a90e2;
          }

          .login-form button {
            padding: 12px 24px;
            background: #4a90e2;
            color: white;
            border: none;
            border-radius: 8px;
            font-size: 16px;
            font-weight: 600;
            cursor: pointer;
            transition: background 0.2s;
          }

          .login-form button:hover:not(:disabled) {
            background: #357abd;
          }

          .login-form button:disabled {
            background: #ccc;
            cursor: not-allowed;
          }

          .auth-error {
            color: #f44336;
            font-size: 14px;
            margin: 0;
            text-align: center;
          }

          .loading-container {
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 200px;
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="guild-war-instructions">
      <h2 className="page-title">CMA Guild War Status</h2>
      <div className="battle-selector">
        <div className="battle-buttons">
          {[1, 2, 3].map(battle => {
            const isAvailable = isBattleAvailable(battle);
            return (
              <button
                key={battle}
                className={`battle-button ${selectedBattle === battle ? 'active' : ''} ${!isAvailable ? 'disabled' : ''}`}
                onClick={() => isAvailable && setSelectedBattle(battle)}
                disabled={!isAvailable}
                title={!isAvailable ? 'This battle is not available yet' : ''}
              >
                Battle {battle}
              </button>
            );
          })}
        </div>
      </div>

      <TabNavigation />

      <div className="tab-content">
        {battleStatusSubTab === 'attackStatus' && <AttackStatusTab />}
        {battleStatusSubTab === 'battleMap' && <BattleStatusTab />}
      </div>

      <style jsx>{`
        .subtab-navigation {
          display: flex;
          justify-content: center;
          margin-bottom: 16px;
          background: #f1f1f1;
          border-radius: 8px;
          padding: 8px;
        }

        .subtab-button {
          padding: 8px 16px;
          margin: 0 8px;
          border: none;
          background: transparent;
          border-radius: 4px;
          cursor: pointer;
          font-weight: 500;
          transition: background-color 0.2s, color 0.2s;
          min-width: 120px;
          text-align: center;
        }

        .subtab-button:hover {
          background: #e0e0e0;
        }

        .subtab-button.active {
          background: #4a90e2;
          color: white;
        }
      `}</style>
    </div>
  );
};

export default GuildWarStatus;
